import { useEffect, useState } from "react";
import {
  Input,
  Label,
  SubmitButton,
  SubmittingHint,
  Textarea,
  useSubmittingMutation,
  useToast,
} from "@/design-system/components/ui";
import { PortalApiError } from "../api/client";
import {
  portalVendorApi,
  type PortalVendorProfileDto,
} from "../api/vendor";

// Step 5 W6 — a vendor edits their own contact details.
//
// Deliberately its OWN section rather than fields added to the shared
// profile block above it: those are PortalUser fields (the login account),
// these are Vendor-record fields (the business Phoenix contracts with), and
// the two are different rows that happen to belong to the same person. A
// vendor changing their office address is not changing their sign-in.
//
// The change applies immediately — there is no staff review step. It is
// audited and Phoenix is notified, which is the right weight for a phone
// number. Email is READ-ONLY: it is the sign-in credential, and the server
// refuses any request that carries one.

export function VendorProfileBlock() {
  const toast = useToast();
  const [loaded, setLoaded] = useState<PortalVendorProfileDto | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const dto = await portalVendorApi.getProfile();
        if (cancelled) return;
        applyLoaded(dto);
      } catch {
        // A vendor account with no linked vendor record 404s here. Nothing
        // to edit, so the section simply doesn't render.
        if (!cancelled) setLoadFailed(true);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function applyLoaded(dto: PortalVendorProfileDto) {
    setLoaded(dto);
    setName(dto.name);
    setPhone(dto.phone ?? "");
    setAddress(dto.address ?? "");
  }

  const save = useSubmittingMutation(
    () =>
      portalVendorApi.updateProfile({
        name: name.trim(),
        phone: phone.trim(),
        address: address.trim(),
      }),
    {
      onSuccess: (dto: PortalVendorProfileDto) => {
        applyLoaded(dto);
        setError(null);
        toast.success("Your details are updated.");
      },
      onError: (err: unknown) => {
        const message =
          err instanceof PortalApiError
            ? err.message
            : err instanceof Error
              ? err.message
              : "Couldn't save your details. Try again.";
        setError(message);
        toast.error(message);
      },
    },
  );

  if (loadFailed || !loaded) return null;

  const dirty =
    name.trim() !== loaded.name ||
    phone.trim() !== (loaded.phone ?? "") ||
    address.trim() !== (loaded.address ?? "");

  return (
    <div
      className="p-space-4 border border-border-light rounded-md bg-white-pure"
      data-testid="settings-vendor-profile"
    >
      <h3 className="text-h4 font-display text-onyx m-0">Your business details</h3>
      <p className="text-small text-gray-mid mt-space-1">
        What Phoenix has on file for your business. Changes save straight away
        — we'll be notified.
      </p>

      <div className="mt-space-4 flex flex-col gap-space-3">
        <div className="space-y-space-1">
          <Label htmlFor="vendor-profile-name">Business name</Label>
          <Input
            id="vendor-profile-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            data-testid="vendor-profile-name"
          />
        </div>
        <div className="space-y-space-1">
          <Label htmlFor="vendor-profile-phone">Phone</Label>
          <Input
            id="vendor-profile-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            data-testid="vendor-profile-phone"
          />
        </div>
        <div className="space-y-space-1">
          <Label htmlFor="vendor-profile-address">Address</Label>
          <Textarea
            id="vendor-profile-address"
            rows={3}
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            data-testid="vendor-profile-address"
          />
        </div>

        <div>
          <span className="text-caption text-gray-mid">Email</span>
          <p className="text-small text-onyx m-0" data-testid="vendor-profile-email">
            {loaded.email ?? "None on file"}
          </p>
          <p className="text-caption text-gray-mid mt-space-1">
            This is also how you sign in, so we change it for you rather than
            risk locking you out. Ask us and we'll update it.
          </p>
        </div>
      </div>

      <div className="mt-space-4 flex items-center gap-space-3">
        <SubmitButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => void save.submit()}
          pending={save.pending}
          pendingLabel="Saving…"
          disabled={!dirty || name.trim().length === 0}
          data-testid="vendor-profile-save"
        >
          Save changes
        </SubmitButton>
        <SubmittingHint pending={save.pending} />
      </div>

      {error && (
        <p className="text-small text-status-error mt-space-2" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
